/**
 * CPF utility functions for normalization and formatting
 */

/**
 * Normalize CPF by removing all non-digit characters
 * Example: "431.247.028-67" -> "43124702867"
 */
export const normalizeCPF = (cpf: string): string => {
  return cpf.replace(/\D/g, '');
};

/**
 * Format CPF to standard format: 000.000.000-00
 * Example: "43124702867" -> "431.247.028-67"
 */
export const formatCPF = (cpf: string): string => {
  const normalized = normalizeCPF(cpf);

  if (normalized.length !== 11) {
    return cpf; // Return original if invalid length
  }

  return normalized.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4');
};

/**
 * Check if a string looks like a CPF (has at least 3 digits)
 */
export const isCPFFormat = (input: string): boolean => {
  const cleaned = normalizeCPF(input);
  return cleaned.length >= 3 && /^\d+$/.test(cleaned);
};

/**
 * Validate CPF checksum digits
 * Returns true if CPF is valid, false otherwise
 */
export const isValidCPF = (cpf: string): boolean => {
  const normalized = normalizeCPF(cpf);

  // Must have exactly 11 digits
  if (normalized.length !== 11) {
    return false;
  }

  // Cannot be all same digits (111.111.111-11, etc.)
  if (/^(\d)\1{10}$/.test(normalized)) {
    return false;
  }

  // Validate first check digit
  let sum = 0;
  for (let i = 0; i < 9; i++) {
    sum += parseInt(normalized.charAt(i)) * (10 - i);
  }
  let checkDigit = 11 - (sum % 11);
  if (checkDigit >= 10) checkDigit = 0;
  if (checkDigit !== parseInt(normalized.charAt(9))) {
    return false;
  }

  // Validate second check digit
  sum = 0;
  for (let i = 0; i < 10; i++) {
    sum += parseInt(normalized.charAt(i)) * (11 - i);
  }
  checkDigit = 11 - (sum % 11);
  if (checkDigit >= 10) checkDigit = 0;
  if (checkDigit !== parseInt(normalized.charAt(10))) {
    return false;
  }

  return true;
};

/**
 * Whether the `cpf` field should be included in a person-update payload.
 *
 * PUT /people/:id validates and duplicate-checks the CPF whenever the field is PRESENT in the
 * body, and skips that entirely when it is absent. Sending it on every save meant a person
 * whose CPF was stored in the old formatted style ("320.179.328-09" — CreateEmployee never
 * normalized) could not be saved at all: the backend normalizes the incoming value, sees it
 * differ from the stored text, runs the duplicate check, and finds the other row holding the
 * same CPF in normalized form. The operator gets "CPF já cadastrado no sistema" on a save
 * where they never touched the field.
 *
 * Comparing on digits only means formatting differences are not mistaken for an edit.
 */
export const shouldSendCpfOnUpdate = (
  nextCpf: string | null | undefined,
  storedCpf: string | null | undefined,
): boolean => {
  const nextRaw = (nextCpf ?? '').trim();
  const nextDigits = normalizeCPF(nextRaw);
  const storedDigits = normalizeCPF(storedCpf ?? '');

  // A MASKED value is never a CPF, whichever mask it is. maskCPF produces two shapes: the
  // digit-less "***.***.**-**" for a malformed stored value, and the partial "***.982.247-**"
  // that keeps the middle six. Testing for "no digits" caught only the first — the partial mask
  // carries six digits, so it compared as an edit and would have been sent, failing the
  // backend's CPF validation on a save where the operator touched nothing. An asterisk settles
  // it for both. A genuinely cleared field is blank, so it still reaches the branch below.
  if (nextRaw.includes('*')) return false;
  if (nextRaw !== '' && nextDigits === '') return false;

  return nextDigits !== storedDigits;
};
